import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Components } from './styled';
import { AppSettingsContext } from '../../providers/AppSettingsProvider/context';
import ProjectContainer from '../../components/containers/ProjectContainer';
import ProjectTable from '../../components/ProjectTable';
import { tableColumns } from '../../components/ProjectTable/constants';
import { getDisplayNames } from '../../api/utils';
import Goals from './components/Goals';
import Categories from './components/Categories';
import useModalState from '../../hooks/useModalState';
import EditPillarModal from '../../components/modals/EditPillarModal';
import { Pillar } from '../../api/types/contentManagement';
import { getAllPillars } from '../../api/contentManagement';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import Authors from './components/Authors';
import Promotions from './components/Promotions';
import AuthorCategories from './components/AuthorsCategories';
import DailyMissionTemplates from './components/DailyMissionTemplates';
import LevelDefinitions from './components/LevelDefinitions';
import MentorBoards from './components/MentorBoards';
import MentorShorts from './components/MentorShorts';
import HomepageShorts from './components/HomepageShorts';

import { allSections, defaultSection, getSectionPath } from './sections';

const ContentManagement = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { pillarExpertises } = useContext(AppSettingsContext);

  const [shouldRefreshContext, setShouldRefreshContext] = useState(false);

  const [pillarsData, setPillarsData] = useState<Pillar[]>([]);

  const pillarModalProps = useModalState<{ pillar: Pillar }>();

  const params = useParams();
  const sectionParam = params['*']?.replace(/\/+$/, '');
  const foundSection = allSections.find((section) => section.key === sectionParam);
  const activeSection = foundSection ?? defaultSection;

  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllPillars();
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setPillarsData(result);
    }
  };

  useEffect(() => {
    refreshData().then();
  }, []);

  const onEditPillar = (id: string) => {
    const foundPillar = pillarsData?.find((item) => item?.id === id);
    if (foundPillar) {
      pillarModalProps.open({ pillar: foundPillar });
    }
  };

  const renderSection = () => {
    switch (activeSection.key) {
      case 'pillars':
        return (
          <Components.LengthInputsWrapper>
            <Components.PillarTableWrapper>
              <ProjectTable
                data={pillarsData || []}
                columns={tableColumns.pillars({ onEdit: onEditPillar })}
                sortingModel={{ field: 'name', sort: 'asc' }}
              />
            </Components.PillarTableWrapper>
            <EditPillarModal {...pillarModalProps} />
            <Components.PillarTableWrapper>
              <ProjectTable
                data={pillarExpertises.map((item) => getDisplayNames.pillarExpertise(item) || '') || []}
                columns={tableColumns.basicSingleText('Pillar expertises')}
                sortingModel={{ field: 'name', sort: 'asc' }}
              />
            </Components.PillarTableWrapper>
          </Components.LengthInputsWrapper>
        );
      case 'goals':
        return <Goals shouldRefreshContext={shouldRefreshContext} setShouldRefreshContext={setShouldRefreshContext} />;
      case 'categories':
        return <Categories setShouldRefreshContext={setShouldRefreshContext} />;
      case 'authors':
        return <Authors />;
      case 'author-categories':
        return <AuthorCategories setShouldRefreshContext={setShouldRefreshContext} />;
      case 'mentor-boards':
        return <MentorBoards setShouldRefreshContext={setShouldRefreshContext} />;
      case 'mentor-shorts':
        return <MentorShorts />;
      case 'homepage-shorts':
        return <HomepageShorts />;
      case 'promotions':
        return <Promotions />;
      case 'daily-missions':
        return <DailyMissionTemplates setShouldRefreshContext={setShouldRefreshContext} />;
      case 'levels':
        return <LevelDefinitions setShouldRefreshContext={setShouldRefreshContext} />;
    }
  };

  if (!foundSection) {
    return <Navigate to={getSectionPath(defaultSection.key)} replace />;
  }

  return (
    <ProjectContainer title={'Content Management'}>
      <Components.Layout>
        <Components.Container>
          <Components.SectionHeaderWrapper>
            <Components.SectionHeader>{activeSection.title}</Components.SectionHeader>
            <Components.SectionDescription>{activeSection.description}</Components.SectionDescription>
          </Components.SectionHeaderWrapper>
          {renderSection()}
        </Components.Container>
      </Components.Layout>
    </ProjectContainer>
  );
};

export default ContentManagement;
